import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { useDeferredAction } from '@common/hooks';
import {
  EMAIL_PLACEHOLDER,
  FIRST_NAME_PLACEHOLDER,
  JOB_PLACEHOLDER,
  LAST_NAME_PLACEHOLDER,
  PHONE_PLACEHOLDER,
} from '@common/schemas';

import {
  createEditFirstAdminSchemaAsyncEmail,
  createEditFirstAndSecondAdminSchemaAsyncEmail,
  EditAdminFormResult,
} from '@common/schemas/forms/organisme';
import {
  OrganismeDetailsDto,
  UpdateOrganismeUtilisateurAdminArgs,
  useLazyIsEmailAdminAvailableQuery,
  useUpdateOrganismeUtilisateursAdminMutation,
  UtilisateurDto,
} from '@common/services';
import { ConfirmationModal, DsfrModal, Form, FormMethodsRef, getModalById, TextField } from '@common/ui';
import { areSomePropertiesDifferent, DateUtils, isSchemaIncludeAnOther } from '@common/utils';
import React, { useEffect, useRef, useState } from 'react';
import { ZodObject } from 'zod';
import { mapEditAdminFormResult } from './utils/edit-admins-mapper';

const confirmationModal = getModalById('second-admin-confirmation-modal');

export const EditOfaAdminsModal = ({
                                     dsfrModal,
                                     organisme,
                                     admin1,
                                     admin2,
                                   }: EditAdminInfoModalProps) => {

  const t = useTextes('Commun', 'Admins');

  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null!);
  const formMethodsRef = useRef<FormMethodsRef<EditAdminFormResult> | null>(null!);

  const [isLastNameChanging, setIsLastNameChanging] = useState(false);

  const { prepareAction, performAction, clearAction, actionExists } = useDeferredAction();

  const [isEmailAdminAvailableQuery] = useLazyIsEmailAdminAvailableQuery();
  const [updateOrganismeUtilisateurAdmin] = useUpdateOrganismeUtilisateursAdminMutation();

  let timeoutEmailId: number | null = null;

  // Verification asynchrone de l'email d'admin lors de l'édition des admins d'un organisme
  const emailAsyncValidation = (email: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (timeoutEmailId !== null) {
        window.clearTimeout(timeoutEmailId);
      }

      timeoutEmailId = window.setTimeout(async () => {
        try {
          const result = await isEmailAdminAvailableQuery({ email, organismeId: organisme.id }).unwrap();
          resolve(result);
        } catch (error) {
          resolve(false);
        }
      }, 500);
    });
  };

  // suppression du timeout avec useEffect lors de la destruction du composant
  useEffect(() => {
    return () => {
      if (timeoutEmailId !== null) {
        clearTimeout(timeoutEmailId);
      }
    };
  }, []);

  const [withSecondAdmin, setWithSecondAdmin] = useState(!!admin2);

  const editFirstAdminSchemaAsyncEmail = createEditFirstAdminSchemaAsyncEmail(emailAsyncValidation);
  const editFirstAndSecondAdminSchemaAsyncEmail = createEditFirstAndSecondAdminSchemaAsyncEmail(emailAsyncValidation);

  const [formSchema, setFormSchema] = useState<ZodObject>(
    withSecondAdmin ? editFirstAndSecondAdminSchemaAsyncEmail : editFirstAdminSchemaAsyncEmail,
  );

  useEffect(() => {
    if (withSecondAdmin) {
      setFormSchema(editFirstAndSecondAdminSchemaAsyncEmail);
    } else {
      setFormSchema(editFirstAdminSchemaAsyncEmail);
    }
  }, [withSecondAdmin]);

  const values: EditAdminFormResult = {
    firstAdminLastName: admin1.nom,
    firstAdminFirstName: admin1.prenom,
    firstAdminJob: admin1.travail,
    firstAdminPhoneNumber: admin1.telephone,
    firstAdminEmail: admin1.email,

    secondAdminLastName: admin2?.nom,
    secondAdminFirstName: admin2?.prenom,
    secondAdminJob: admin2?.travail,
    secondAdminPhoneNumber: admin2?.telephone,
    secondAdminEmail: admin2?.email,
  } as EditAdminFormResult;

  async function handleSubmit(result: EditAdminFormResult) {
    const { firstAdminResult, secondAdminResult } = mapEditAdminFormResult(result);

    // on vérifie s'il y a eu des changements concernant les données de l'admin 1 ou de l'admin 2

    const isAdmin1NomChange = admin1.nom !== firstAdminResult?.nom;
    const isAdmin2NomChange = admin2 && secondAdminResult && admin2.nom !== secondAdminResult.nom;
    const isAdmin2Added = !admin2 && secondAdminResult;
    const isAdmin2Deleted = admin2 && !secondAdminResult;

    const isAdmin1ChangeExceptNom = areSomePropertiesDifferent(admin1, firstAdminResult, ['prenom', 'travail', 'telephone', 'email']) && !isAdmin1NomChange;
    const isAdmin2ChangeExceptNom = admin2 && secondAdminResult && areSomePropertiesDifferent(admin2, secondAdminResult, ['prenom', 'travail', 'telephone', 'email']) && !isAdmin2NomChange;

    const updateAdminsReqBody: UpdateOrganismeUtilisateurAdminArgs = {
      id: organisme.id,
      utilisateursAdminDto: {
        admin1: firstAdminResult,
        admin2: secondAdminResult,
      },
    };

    // Conditions qui impliquent une nouvelle habilitation et qui nécessitent donc une double confirmation
    if (isAdmin1NomChange || isAdmin2NomChange || isAdmin2Added || isAdmin2Deleted) {
      prepareAction(() => updateOrganismeUtilisateurAdmin(updateAdminsReqBody));
      confirmationModal.open();
    }

    // Conditions qui n'impliquent pas de nouvelle habilitation
    else if (isAdmin1ChangeExceptNom || isAdmin2ChangeExceptNom) {
      await updateOrganismeUtilisateurAdmin(updateAdminsReqBody).unwrap();
      setIsLastNameChanging(false);
    }

    dsfrModal.close();
  }

  const handleSecondModalConfirmation = async () => {
    await performAction();
    setIsLastNameChanging(false);
    confirmationModal.close();
  };

  const handleCancelSecondModalConfirmation = () => {
    confirmationModal.close();
    if (actionExists) {
      // Si une action est toujours enregistrée dans le hook, ça veut dire qu'elle n'a pas été consommée
      // Et il faut donc rouvrir la modale principale
      dsfrModal.open();
    }
    clearAction();
  };

  return (
    <>
      <dsfrModal.Component
        title={''}
        size="large"
        topAnchor={false}
        buttons={[
          {
            children: 'ANNULER',
            className: 'text-red-400!',
            doClosesModal: true,
            priority: 'tertiary',
            onClick: () => formMethodsRef.current?.reset(values),
          },
          {
            children: 'VALIDER',
            doClosesModal: false,
            priority: 'primary',
            onClick: () => buttonSubmitRef.current?.click(),
          },
        ]}
      >
        <div className={'flex flex-col p-6'}>

          <div className={'flex flex-col gap-6'}>

            <h4 className={'m-0!'}>Modification des administrateurs</h4>

            <span
              className={'text-sm'}>Dernière connexion : {DateUtils.formatDdMmYyyy(admin1.dateConnexion ?? '')}</span>

            <span className={'text-sm text-[#484D7A] mb-10'}>Les champs marqués d’un * sont obligatoires</span>
          </div>

          <Form<EditAdminFormResult>
            schema={formSchema}
            onSubmit={handleSubmit}
            className={'admin-form pt-6'}
            values={values}
            buttonSubmitRef={buttonSubmitRef}
            methodsRef={formMethodsRef}
          >
            <div className={'flex flex-col gap-4'}>

              <span className={'font-bold'}>Administrateur 1 :</span>

              <TextField name="firstAdminLastName"
                         title="Nom"
                         placeholder={LAST_NAME_PLACEHOLDER}
                         tooltip={t['DONNEE_OFA_COMP_ADMIN1_NOM_INFO_BULLE_MSG']}
                         onChange={() => setIsLastNameChanging(true)} />

              <TextField name="firstAdminFirstName"
                         title="Prénom"
                         placeholder={FIRST_NAME_PLACEHOLDER}
                         tooltip={t['DONNEE_OFA_COMP_ADMIN1_PRENOM_INFO_BULLE_MSG']} />

              <TextField name="firstAdminJob"
                         title="Fonction"
                         placeholder={JOB_PLACEHOLDER} />

              <TextField name="firstAdminPhoneNumber"
                         title="Téléphone"
                         placeholder={PHONE_PLACEHOLDER}
                         tooltip={t['DONNEE_OFA_COMP_ADMIN1_TELEPHONE_INFO_BULLE_MSG']} />

              <TextField name="firstAdminEmail"
                         title="Courriel"
                         placeholder={EMAIL_PLACEHOLDER}
                         tooltip={t['DONNEE_OFA_COMP_ADMIN1_COURRIEL_INFO_BULLE_MSG']} />

              {(!withSecondAdmin) && <Button
                iconId={'fr-icon-add-line'}
                priority={'secondary'}
                type={'button'}
                onClick={() => setWithSecondAdmin(true)}
              >
                AJOUTER UN ADMINISTRATEUR
              </Button>
              }
            </div>

            {(withSecondAdmin && isSchemaIncludeAnOther(formSchema, editFirstAndSecondAdminSchemaAsyncEmail)) && (
              <div className={'flex flex-col gap-4 mt-6'}>
                <div className={'flex justify-between'}>
                  <span className={'font-bold'}>Administrateur 2 :</span>
                  <span
                    onClick={() => setWithSecondAdmin(false)}
                    className={'fr-link underline font-bold hover:cursor-pointer'}
                  >
                SUPPRIMER L'ADMINISTRATEUR
              </span>
                </div>

                <TextField name="secondAdminLastName"
                           title="Nom"
                           placeholder={LAST_NAME_PLACEHOLDER}
                           tooltip={t['DONNEE_OFA_COMP_ADMIN2_NOM_INFO_BULLE_MSG']}
                           onChange={() => setIsLastNameChanging(true)} />

                <TextField name="secondAdminFirstName"
                           title="Prénom"
                           placeholder={FIRST_NAME_PLACEHOLDER}
                           tooltip={t['DONNEE_OFA_COMP_ADMIN2_PRENOM_INFO_BULLE_MSG']} />

                <TextField name="secondAdminJob"
                           title="Fonction"
                           placeholder={JOB_PLACEHOLDER} />

                <TextField name="secondAdminPhoneNumber"
                           title="Téléphone"
                           placeholder={PHONE_PLACEHOLDER}
                           tooltip={t['DONNEE_OFA_COMP_ADMIN2_TELEPHONE_INFO_BULLE_MSG']} />

                <TextField name="secondAdminEmail"
                           title="Courriel"
                           placeholder={EMAIL_PLACEHOLDER} />
              </div>
            )}
          </Form>

          {isLastNameChanging && (
            <span
              className={'mt-6 text-red-500'}>{'Attention : en cas de modification d\'un administrateur, la fiche d’habilitation est mise à jour. Le représentant légal la valide par courriel, puis elle est validée par France compétences. Pendant ce délai, l’accès à MAP est indisponible.'}</span>
          )}

        </div>
      </dsfrModal.Component>
      <ConfirmationModal dsfrModal={confirmationModal}
                         description={t['DONNEE_OFA_COMP_ADMIN_MODIF_INFO_BULLE_MSG']}
                         subDescription={t['DONNEE_OFA_COMP_ADMIN_MODIF_INFO_BULLE_SUB_MSG']}
                         confirmProps={{ onClick: () => handleSecondModalConfirmation() }}
                         cancelProps={{ onClick: () => handleCancelSecondModalConfirmation() }} />
    </>
  );
};

interface EditAdminInfoModalProps {
  dsfrModal: DsfrModal;
  organisme: OrganismeDetailsDto;
  admin1: UtilisateurDto;
  admin2?: UtilisateurDto;
}
